import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import http from "services/api";

const PORTAL_URL = "https://credenciado.aliviaapp.com.br";
const FORM_VAZIO = { nome: "", email: "", password: "" };

function gerarMensagem(email, password, empresaNome) {
  return `Olá! 👋\n\nSegue seu acesso ao *Portal de Empresas Credenciadas* — *${empresaNome}*:\n\n🌐 *Site:* ${PORTAL_URL}\n👤 *Usuário:* ${email}\n🔑 *Senha:* ${password}\n\nAcesse o link, faça login e gerencie as vidas da sua empresa.\n\nQualquer dúvida estamos à disposição! 😊`;
}

/* ─── Overlay ─── */
const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 1400;
  background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
  animation: fadeIn 0.18s ease;
  @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
`;

const Sheet = styled.div`
  background: #fff; border-radius: 16px;
  width: 100%; max-width: 480px;
  max-height: 88vh; overflow-y: auto;
  animation: slideUp 0.22s ease;
  @keyframes slideUp { from { opacity:0; transform:translateY(12px) } to { opacity:1; transform:translateY(0) } }
`;

/* ─── Layout interno ─── */
const Header = styled.div`
  padding: 24px 24px 0;
  display: flex; align-items: center; justify-content: space-between;
`;

const Body = styled.div`padding: 20px 24px;`;
const Footer = styled.div`
  padding: 16px 24px 24px;
  display: flex; gap: 10px;
  border-top: 1px solid #f0f0f0;
`;

/* ─── Tabs ─── */
const TabRow = styled.div`
  display: flex; border-bottom: 1px solid #f0f0f0;
  margin: 16px 24px 0;
`;
const TabBtn = styled.button`
  flex: 1; padding: 10px 0; font-size: 13px; font-weight: 600;
  font-family: inherit; cursor: pointer; border: none; background: transparent;
  color: ${p => p.active ? "#111" : "#999"};
  border-bottom: 2px solid ${p => p.active ? "#111" : "transparent"};
  transition: all 0.15s;
  &:hover { color: #111; }
`;

/* ─── Inputs ─── */
const FieldWrap = styled.div`margin-bottom: 16px;`;
const Label = styled.label`
  display: block; font-size: 11px; font-weight: 700;
  letter-spacing: 0.07em; text-transform: uppercase;
  color: #888; margin-bottom: 6px;
`;
const Input = styled.input`
  width: 100%; padding: 11px 14px; font-size: 14px;
  font-family: inherit; border-radius: 10px;
  border: 1.5px solid #e8e8e8; background: #fafafa;
  outline: none; transition: border 0.15s, background 0.15s;
  color: #111;
  &:focus { border-color: #111; background: #fff; }
  &::placeholder { color: #bbb; }
`;
const PassWrap = styled.div`position: relative;`;
const EyeBtn = styled.button`
  position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; color: #aaa;
  font-size: 16px; padding: 0; line-height: 1;
  &:hover { color: #555; }
`;

/* ─── Botões ─── */
const Btn = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  gap: 7px; padding: 11px 20px; border-radius: 10px;
  font-size: 13px; font-weight: 600; font-family: inherit;
  cursor: pointer; border: none; transition: all 0.15s;
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;
const BtnDark = styled(Btn)`
  background: #111; color: #fff; flex: 1;
  &:hover:not(:disabled) { background: #333; }
`;
const BtnOutline = styled(Btn)`
  background: #fff; color: #111; flex: 1;
  border: 1.5px solid #e0e0e0;
  &:hover:not(:disabled) { background: #f5f5f5; }
`;
const BtnGreen = styled(Btn)`
  background: #25d366; color: #fff;
  &:hover:not(:disabled) { background: #1ebe57; }
`;
const BtnGhost = styled.button`
  background: none; border: none; cursor: pointer;
  color: #aaa; font-size: 20px; line-height: 1;
  padding: 0; transition: color 0.15s;
  &:hover { color: #111; }
`;
const BtnIconRed = styled.button`
  background: none; border: none; cursor: pointer; padding: 4px;
  color: #ccc; font-size: 17px; transition: color 0.15s; border-radius: 6px;
  &:hover { color: #e53935; background: #fff0f0; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

/* ─── Cards e misc ─── */
const ErrBox = styled.div`
  background: #fff5f5; border: 1px solid #fcc; border-radius: 10px;
  padding: 11px 14px; font-size: 13px; color: #c0392b; margin-bottom: 16px;
`;
const CredCard = styled.div`
  background: #f7f8ff; border: 1px solid #e8eaff;
  border-radius: 12px; padding: 18px; margin-bottom: 14px;
`;
const InfoRow = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-top: 12px;
`;
const InfoItem = ({ label, value, mono }) => (
  <div>
    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#aaa", marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 13, color: "#111", fontFamily: mono ? "monospace" : "inherit", fontWeight: mono ? 700 : 500 }}>{value}</div>
  </div>
);

const WppBox = styled.div`
  background: #f0faf3; border: 1px solid #c3e6cb;
  border-radius: 12px; padding: 16px; margin-bottom: 14px; cursor: pointer;
  &:hover { background: #e6f7ec; }
`;
const WppPre = styled.pre`
  font-family: inherit; font-size: 12.5px; color: #1a5e34;
  white-space: pre-wrap; line-height: 1.75; margin: 0; user-select: none;
`;
const ActionRow = styled.div`display: flex; gap: 8px; flex-wrap: wrap;`;

const UserItem = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f5f5f5;
  &:last-child { border-bottom: none; }
`;
const Avatar = styled.div`
  width: 36px; height: 36px; border-radius: 50%;
  background: #111; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; font-weight: 700; flex-shrink: 0;
`;
const Empty = styled.div`
  text-align: center; padding: 40px 0; color: #bbb; font-size: 13px;
`;
const Spinner = () => (
  <span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid #fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }}>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </span>
);
const SpinnerDark = () => (
  <span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid #ddd", borderTop: "2px solid #555", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
);

export default function ModalCriarCredenciado({ open, empresa, onClose }) {
  const [aba, setAba] = useState(0);
  const [form, setForm] = useState(FORM_VAZIO);
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [criado, setCriado] = useState(null);
  const [copiado, setCopiado] = useState("");

  const [usuarios, setUsuarios] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [removendoId, setRemovendoId] = useState(null);

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

  const isValid =
    form.nome.trim().length >= 3 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
    form.password.length >= 6;

  async function carregarUsuarios() {
    if (!empresa?.id) return;
    setLoadingUsuarios(true);
    try {
      const res = await http.get(`/api/v1/adm/usuarios/credenciados/${empresa.id}`);
      setUsuarios(res.data.usuarios || []);
    } catch (_) {}
    finally { setLoadingUsuarios(false); }
  }

  useEffect(() => { if (open && empresa?.id) carregarUsuarios(); }, [open, empresa?.id]);

  async function handleSubmit() {
    setErro(""); setLoading(true);
    try {
      await http.post("/api/v1/adm/usuarios/cadastro-credenciado", { nome: form.nome, email: form.email, password: form.password, empresa_id: empresa.id });
      setCriado({ nome: form.nome, email: form.email, password: form.password });
      carregarUsuarios();
    } catch (e) {
      setErro(e.response?.data?.titulo || e.response?.data?.message || "Erro ao criar acesso");
    } finally { setLoading(false); }
  }

  async function handleDesativar(uid, nome) {
    if (!window.confirm(`Desativar acesso de ${nome}?`)) return;
    setRemovendoId(uid);
    try {
      await http.delete(`/api/v1/adm/usuarios/credenciados/${uid}`);
      carregarUsuarios();
    } catch (_) {}
    finally { setRemovendoId(null); }
  }

  function reset() { setForm(FORM_VAZIO); setCriado(null); setErro(""); setShowPass(false); }
  function fechar() { reset(); setAba(0); onClose(); }

  async function copiar(texto, key) {
    await navigator.clipboard.writeText(texto);
    setCopiado(key);
    setTimeout(() => setCopiado(""), 2200);
  }

  if (!open) return null;

  const mensagem = criado ? gerarMensagem(criado.email, criado.password, empresa?.nome) : "";

  /* ── Tela de sucesso ── */
  if (criado) return (
    <Overlay onClick={(e) => e.target === e.currentTarget && fechar()}>
      <Sheet>
        <Header>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#111" }}>✅ Acesso criado!</div>
            <div style={{ fontSize: 13, color: "#888", marginTop: 2 }}>Compartilhe com o responsável da empresa</div>
          </div>
          <BtnGhost onClick={fechar}>×</BtnGhost>
        </Header>

        <Body>
          <CredCard>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#aaa" }}>Empresa</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#111", marginTop: 2 }}>{empresa?.nome}</div>
            <div
              onClick={() => copiar(PORTAL_URL, "url")}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                marginTop: 6, marginBottom: 4,
                background: "#f0f4ff", border: "1px solid #d4ddff",
                borderRadius: 8, padding: "5px 12px", cursor: "pointer",
                transition: "background 0.15s",
              }}
              title="Clique para copiar o link"
            >
              <span style={{ fontSize: 12, color: "#3b6fd4", fontWeight: 600 }}>{PORTAL_URL}</span>
              <span style={{ fontSize: 11, color: copiado === "url" ? "#16a34a" : "#aaa", fontWeight: 700 }}>
                {copiado === "url" ? "✓" : "⎘"}
              </span>
            </div>
            <InfoRow>
              <InfoItem label="Nome" value={criado.nome} />
              <InfoItem label="Email" value={criado.email} />
              <InfoItem label="Senha" value={criado.password} mono />
              <InfoItem label="Usuário para login" value={criado.email} />
            </InfoRow>
          </CredCard>

          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#aaa", marginBottom: 8 }}>
            📱 Mensagem pronta — clique para copiar
          </div>
          <WppBox onClick={() => copiar(mensagem, "wpp")}>
            <WppPre>{mensagem}</WppPre>
            {copiado === "wpp" && <div style={{ fontSize: 11, color: "#1a5e34", fontWeight: 700, marginTop: 8 }}>Copiado!</div>}
          </WppBox>

          <ActionRow>
            <BtnOutline style={{ flex: "unset", padding: "9px 16px" }} onClick={() => copiar(`Email: ${criado.email}\nSenha: ${criado.password}\nSite: ${PORTAL_URL}`, "cred")}>
              {copiado === "cred" ? "✓ Copiado" : "📋 Credenciais"}
            </BtnOutline>
            <BtnOutline style={{ flex: "unset", padding: "9px 16px" }} onClick={() => copiar(mensagem, "wpp")}>
              {copiado === "wpp" ? "✓ Copiado" : "📝 Mensagem"}
            </BtnOutline>
            <BtnGreen style={{ flex: "unset", padding: "9px 16px" }}
              onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(mensagem)}`, "_blank")}>
              WhatsApp
            </BtnGreen>
          </ActionRow>
        </Body>

        <Footer>
          <BtnOutline onClick={reset}>+ Criar outro</BtnOutline>
          <BtnDark onClick={fechar}>Fechar</BtnDark>
        </Footer>
      </Sheet>
    </Overlay>
  );

  /* ── Modal com abas ── */
  return (
    <Overlay onClick={(e) => e.target === e.currentTarget && fechar()}>
      <Sheet>
        <Header>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#111" }}>Acessos Credenciados</div>
            <div style={{ fontSize: 12, color: "#999", marginTop: 1 }}>{empresa?.nome}</div>
             <div
              onClick={() => copiar(PORTAL_URL, "url")}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                marginTop: 6, marginBottom: 4,
                background: "#f0f4ff", border: "1px solid #d4ddff",
                borderRadius: 8, padding: "5px 12px", cursor: "pointer",
                transition: "background 0.15s",
              }}
              title="Clique para copiar o link"
            >
              <span style={{ fontSize: 12, color: "#3b6fd4", fontWeight: 600 }}>{PORTAL_URL}</span>
              <span style={{ fontSize: 11, color: copiado === "url" ? "#16a34a" : "#aaa", fontWeight: 700 }}>
                {copiado === "url" ? "✓" : "⎘"}
              </span>
            </div>
          </div>
          <BtnGhost onClick={fechar}>×</BtnGhost>
        </Header>

        <TabRow>
          <TabBtn active={aba === 0} onClick={() => setAba(0)}>Criar acesso</TabBtn>
          <TabBtn active={aba === 1} onClick={() => setAba(1)}>
            Usuários {usuarios.length > 0 && `(${usuarios.length})`}
          </TabBtn>
        </TabRow>

        {/* ── Aba Criar ── */}
        {aba === 0 && (
          <>
            <Body>
              {erro && <ErrBox>{erro}</ErrBox>}
              <FieldWrap>
                <Label>Nome completo</Label>
                <Input autoFocus placeholder="Ex: João da Silva" value={form.nome} onChange={set("nome")} maxLength={100} />
              </FieldWrap>
              <FieldWrap>
                <Label>Email</Label>
                <Input type="email" placeholder="email@empresa.com" value={form.email} onChange={set("email")} maxLength={100} />
              </FieldWrap>
              <FieldWrap>
                <Label>Senha</Label>
                <PassWrap>
                  <Input
                    type={showPass ? "text" : "password"}
                    placeholder="Mínimo 6 caracteres"
                    value={form.password} onChange={set("password")}
                    style={{ paddingRight: 42 }}
                  />
                  <EyeBtn type="button" onClick={() => setShowPass(v => !v)}>
                    {showPass ? "🙈" : "👁"}
                  </EyeBtn>
                </PassWrap>
              </FieldWrap>
            </Body>
            <Footer>
              <BtnOutline onClick={fechar}>Cancelar</BtnOutline>
              <BtnDark onClick={handleSubmit} disabled={!isValid || loading}>
                {loading ? <><Spinner /> Criando...</> : "Criar acesso"}
              </BtnDark>
            </Footer>
          </>
        )}

        {/* ── Aba Usuários ── */}
        {aba === 1 && (
          <>
            <Body style={{ minHeight: 200 }}>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                <BtnGhost onClick={carregarUsuarios} style={{ fontSize: 16 }} title="Atualizar">↻</BtnGhost>
              </div>

              {loadingUsuarios ? (
                <Empty><SpinnerDark /></Empty>
              ) : usuarios.length === 0 ? (
                <Empty>Nenhum acesso criado para esta empresa</Empty>
              ) : (
                usuarios.map((u) => (
                  <UserItem key={u.id}>
                    <Avatar>{u.nome?.charAt(0).toUpperCase()}</Avatar>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#111", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.nome}</div>
                      <div style={{ fontSize: 12, color: "#888" }}>{u.email}</div>
                      <div style={{ fontSize: 11, color: "#bbb" }}>Criado em {dayjs(u.created_at).format("DD/MM/YYYY")}</div>
                    </div>
                    <BtnIconRed
                      onClick={() => handleDesativar(u.id, u.nome)}
                      disabled={removendoId === u.id}
                      title="Desativar acesso"
                    >
                      {removendoId === u.id ? <SpinnerDark /> : "🗑"}
                    </BtnIconRed>
                  </UserItem>
                ))
              )}
            </Body>
            <Footer>
              <BtnOutline onClick={fechar}>Fechar</BtnOutline>
              <BtnDark onClick={() => setAba(0)}>+ Criar novo</BtnDark>
            </Footer>
          </>
        )}
      </Sheet>
    </Overlay>
  );
}